import {
  CreateTradeLimits,
  Direction,
  FixMeLater,
  FromEntries,
  MarginTypes,
  OrderStatus,
  PrecisionConfig,
  RawFiltersData,
} from "@shared/types";

export interface GetSignalsRequest {
  is_open: boolean;
  sub_group_id: number;
  for_admin: boolean;
}

export interface GetExchangeSignalByIdRequest {
  exchange_signal_id: number;
  for_admin: boolean;
}

export interface GetSignalByIdRequest {
  signal_id: number;
  for_admin: boolean;
}

export interface SignalExchange {
  signal_cards: any[];
}

export type SignalExtraInfoTuple = [
  ["Long/Short", Direction],
  ["Leverage", number | null],
  ["Margin Type", MarginTypes | null],
  ["Signal Type", string],
  ["Group", string],
  ["Exchanges", string],
  ["Risk/Reward", string],
  ["Potential Profit", string],
  ["Potential Profit Ratio", number],
  ["Entry Zone Category", number]
];

export interface BaseFollowSignalData {
  trade_exists?: number[];
  allow_follow?: number[];
  requires_update?: number[];
  requires_cancel?: number[];
  bot_trade_exists?: number[];
  bot_allow_follow?: number[];
  bot_requires_update?: number[];
  bot_requires_cancel?: number[];
}

export interface BaseSignalData {
  symbol: string;
  is_open: boolean;
  sub_group_id: number;
  signal_id: number;
}

export type FollowSignalData = BaseFollowSignalData & BaseSignalData;

export interface RawZippedSignal extends BaseFollowSignalData {
  exchange_signal_id: number;
  price: number;
  num_of_trades: number;
  trading_volume: number;
  graph_data: GraphData;
  exchange?: string;
  signal_extra_data: SignalExtraInfoTuple;
  relevant_time: number;
  signal_id: number;
  symbol: string;
}

export interface Signal
  extends Omit<RawZippedSignal, "signal_extra_data">,
    BaseSignalData {
  exchanges: string[];
  signal_extra_data: FromEntries<SignalExtraInfoTuple>;
}

export type SignalDataWithKey = Signal & {
  key: string | number;
};

export interface GraphData {
  time_passed: string;
  entry_progress: number;
  take_profit_progress: number;
  profit: number;
}

type SignalGeneralDataTuple = [
  ["Long/Short", "Long" | "Short"],
  ["Signal Type", string],
  ["Exchanges", string],
  ["Group", string],
  ["Opened at", number],
  ["Number of open trades", number],
  ["Number of trades", number],
  ["Open Trades Volume", number],
  ["Published by", string],
  ["Closed at", number]
];

export interface SignalExtraDataResponse
  extends Pick<BaseSignalData, "signal_id" | "symbol" | "sub_group_id"> {
  general: SignalGeneralDataTuple;
  exchange_signal_cards: Record<number, BaseFollowSignalData>;
  graph_data: GraphData;
  precision: PrecisionConfig;
}

export type SignalProcessedGeneralData = FromEntries<SignalGeneralDataTuple>;

interface SignalGeneralData extends BaseSignalData {
  general: SignalProcessedGeneralData;
  exchange_signal_cards: BaseFollowSignalData;
}

export type SignalGeneralExtraData = Pick<
  SignalExtraDataResponse,
  "graph_data" | "precision"
> &
  SignalGeneralData;

export interface TargetCardData {
  id: number;
  number: number;
  status: OrderStatus;
  target_price: string;
  target_extra_data: FixMeLater;
  ratio: number | null;
}

export type unZippedTargetCard = (string | number | string[])[];

export interface RawUnZippedSignalTargets {
  symbol?: string;
  current_price?: number;
  reverse?: boolean;
  leverage?: any;
  breakout?: boolean;
  price_coin?: string;
  stop_target_cards: unZippedTargetCard[];
  entry_target_cards: unZippedTargetCard[];
  take_profit_target_cards: unZippedTargetCard[];
  entry_zones?: RawTargetZone[];
  take_profit_zones?: RawTargetZone[];
  stop_zones?: RawTargetZone[];
}

export type ProcessedSignalTargets = Omit<
  RawUnZippedSignalTargets,
  "entry_target_cards" | "take_profit_target_cards" | "stop_target_cards"
> & {
  stop_target_cards: TargetCardData[];
  entry_target_cards: TargetCardData[];
  take_profit_target_cards: TargetCardData[];
  limits?: EditSignalLimits;
};

export type EditSignalLimits = Pick<CreateTradeLimits, "number_of_targets"> & {
  price: { min: number; max: number };
};

export interface RawTargetZone {
  min: string;
  max: string;
  status: string;
  ratio: number;
  price_reached: number;
}

export type GetSignalsResponse = {
  signals: Signal[];
  filters?: RawFiltersData;
  precision?: PrecisionConfig;
};

export interface GetSignalsSubGroupsResponse {
  data: [number, string, string];
  headers: string[];
}

interface TargetSectionItem {
  price: number;
  ratio: number | null;
  is_closed: boolean;
  number: number;
}

export interface EditSignalRequest {
  signal_id: number;
  buy_section: TargetSectionItem[];
  sell_section: TargetSectionItem[];
  stop_section: TargetSectionItem[];
  buy_zone?: [number, number] | [];
  sell_zone?: [number, number] | [];
  stop_zone?: [number, number] | [];
}

export interface EditSignalResponse {
  new_signal_id: number;
}

export interface CancelSignalRequest {
  signal_id: number;
}

export interface OneClickFollowSignalRequest {
  exchange_signal_id: number;
  bot_id?: number;
  client_id?: number;
}

export interface OneClickFollowSignalResponse {
  new_trade_id: number;
}

export interface SignalExtraDetailsConfiguration {
  data?: SignalConfigurationItem[];
  name?: Name[];
}

export interface SignalConfigurationItem {
  name?: Name[];
  data?: SignalConfigurationField[];
  is_section_header?: FieldData<boolean>[];
}

export interface Name {
  constraints?: FixMeLater;
  outcome?: string;
}

export interface SignalConfigurationField {
  name?: Name[];
  field_type?: FieldData<number>[];
  prefix?: FieldData<string>[];
  prefix_type?: FieldData<number>[];
  value?: FieldData<number>[];
  options?: SignalOptions[];
  default_display?: FieldData<string>[];
}

interface FieldData<T> {
  constraints?: FixMeLater;
  outcome?: T;
}

export interface SignalOptions {
  constraints?: FixMeLater;
  outcome?: Outcome[];
}

export interface Outcome {
  value?: FixMeLater;
  name?: string;
}

export enum EntryZoneCategory {
  BELOW_ENTRY_ZONE = 1,
  BOTTOM_ENTRY_ZONE = 2,
  MID_ENTRY_ZONE = 3,
  TOP_ENTRY_ZONE = 4,
  ABOVE_ENTRY_ZONE = 5,
}
